import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { config } from '../../config/env';
import {
  RegistrationPageProps,
  RegistrationState,
  RegistrationFormData,
  RegistrationStep
} from '../../components/registration/types';
import {
  RegistrationForm,
  RegistrationProgress
} from '../../components/registration';
import { logger } from '../../utils/logger';
import { authInterceptor } from '../../services/Authentication/authInterceptor';
import { secureStorage } from '../../services/Storage/secureStorage';
import { useSEO } from '../../hooks';
import { useAuthContext } from '../../providers';

const RegistrationContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${theme.colors.gradients.primary};
  padding: 0;
  
  @media (min-width: 768px) {
    padding: 2rem 0;
  }
`;

const RegistrationCard = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.heavy};
  overflow: hidden;
`;

const Header = styled.div`
  background: ${theme.colors.gradients.primary};
  color: ${theme.colors.text.light};
  padding: 2rem;
  text-align: center;
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const HeaderSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
`;

const Content = styled.div`
  width: 100%;
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.1);
  color: ${theme.colors.error};
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 2rem;
  text-align: center;
  border: 1px solid ${theme.colors.error};
`;

export const Registration: React.FC<RegistrationPageProps> = ({
  onRegistrationComplete: _onRegistrationComplete,
  onBackToLogin
}) => {
  useSEO({
    title: 'Sign Up | Plenty of Hearts',
    description: 'Create your Plenty of Hearts account and start playing games, planning dates, and chatting with matches who share your interests.',
    canonical: '/registration',
  });

  const navigate = useNavigate();
  const { login } = useAuthContext();

  const [registrationState, setRegistrationState] = useState<RegistrationState>({
    currentStep: 1,
    totalSteps: 1,
    formData: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
    },
    loading: false,
    error: undefined,
    completedSteps: []
  });

  // Simplified registration steps - only Account Details
  const steps: RegistrationStep[] = [
    {
      id: 'account_details',
      title: 'Account Details',
      description: 'Basic information to get you started',
      completed: registrationState.completedSteps.includes('account_details'),
      current: true
    }
  ];

  const updateRegistrationState = (updates: Partial<RegistrationState>) => {
    setRegistrationState(prev => ({ ...prev, ...updates }));
  };

  const updateFormData = (field: keyof RegistrationFormData, value: any) => {
    updateRegistrationState({
      formData: { ...registrationState.formData, [field]: value }
    });
  };

  const handleFormSubmit = async (formData: RegistrationFormData) => {
    updateRegistrationState({ loading: true, error: undefined });

    try {
      const requestPayload = {
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        date_of_birth: formData.dateOfBirth,
        gender: formData.gender,
      };

      // Mock credentials mode - accept any registration without backend call
      if (config.dev.mockCredentials) {
        logger.log('🔓 Mock credentials mode: Registration successful with any credentials');

        // Auto-login the user in mock mode
        const mockUser = {
          id: `mock-user-${formData.email.replace(/[^a-zA-Z0-9]/g, '')}`,
          email: formData.email,
          user_metadata: {
            role: 'user',
            display_name: formData.firstName || formData.email.split('@')[0] || 'Demo User',
            first_name: formData.firstName,
            last_name: formData.lastName,
          },
          app_metadata: {
            provider: 'email',
          },
          created_at: new Date().toISOString(),
        };

        // Create mock tokens (JWT-like format for better compatibility)
        const mockTokenHeader = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
        const mockTokenPayload = btoa(JSON.stringify({
          sub: mockUser.id,
          email: formData.email,
          exp: Math.floor(Date.now() / 1000) + 3600 * 24, // 24 hours expiration
          iat: Math.floor(Date.now() / 1000),
          role: 'user'
        }));
        const mockTokenSignature = btoa('mock-signature');
        const mockAccessToken = `${mockTokenHeader}.${mockTokenPayload}.${mockTokenSignature}`;
        const mockRefreshToken = `mock-refresh-${Date.now()}-${mockUser.id}`;

        // Store mock tokens
        authInterceptor.saveTokens(mockAccessToken, mockRefreshToken);
        secureStorage.saveUserData(mockUser);

        // Complete registration and trigger completion callback
        updateRegistrationState({
          loading: false,
          completedSteps: [...registrationState.completedSteps, 'registration_complete'],
        });

        // Navigate to onboarding after successful mock registration
        setTimeout(() => {
          window.location.href = '/onboarding';
        }, 500);

        return;
      }

      logger.log('Registration request payload:', JSON.stringify(requestPayload, null, 2));
      logger.log('Registration API URL:', `${config.api.baseUrl}/auth/register`);

      // Register user with backend
      const response = await fetch(`${config.api.baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${config.supabase.anonKey}`
        },
        body: JSON.stringify(requestPayload)
      });

      const result = await response.json();
      logger.log('Registration response status:', response.status);
      logger.log('Registration response:', result);

      if (result.success) {
        // DO NOT ASK FOR A CONFIRMATION THAT WAS NEVER REQUIRED.
        //
        // This showed "Check Your Email" for every successful registration.
        // With "Confirm email" disabled on the Supabase project, no email is
        // sent and the account is confirmed and signed in the moment it is
        // created — so the screen was a dead end: no message would ever
        // arrive, and the user could not proceed even though their account
        // was ready. The backend now reports which case it is.
        const needsConfirmation = result.data?.requiresEmailConfirmation !== false;

        if (needsConfirmation) {
          updateRegistrationState({
            loading: false,
            completedSteps: [...registrationState.completedSteps, 'email_verification_pending'],
            showVerificationMessage: true,
            verificationEmail: formData.email
          });
        } else {
          // Already confirmed: sign in with the credentials just entered and
          // let the app's own onboarding gate decide where they land.
          try {
            await login(formData.email, formData.password);
          } catch (loginError) {
            logger.error('Post-registration sign-in failed:', loginError);
          }
          updateRegistrationState({ loading: false });
          navigate('/social-feed');
        }
      } else {
        updateRegistrationState({
          error: result.message || 'Registration failed',
          loading: false
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      updateRegistrationState({
        error: 'Registration failed. Please try again.',
        loading: false
      });
    }
  };

  const renderCurrentStep = () => {
    return (
      <RegistrationForm
        formData={registrationState.formData}
        onFieldChange={updateFormData}
        onSubmit={handleFormSubmit}
        onBack={onBackToLogin}
        loading={registrationState.loading}
        error={registrationState.error}
      />
    );
  };

  return (
    <RegistrationContainer>
      <RegistrationCard>
        <Header>
          <HeaderTitle>Join Plenty of Hearts</HeaderTitle>
          <HeaderSubtitle>
            Create your account and start finding meaningful connections
          </HeaderSubtitle>
        </Header>

        <Content>
          <RegistrationProgress
            currentStep={registrationState.currentStep}
            totalSteps={registrationState.totalSteps}
            steps={steps}
          />

          {registrationState.error && (
            <ErrorMessage>{registrationState.error}</ErrorMessage>
          )}

          {registrationState.showVerificationMessage ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <h2 style={{ color: '#333', marginBottom: '1rem' }}>Check Your Email</h2>
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                We've sent a verification link to <strong>{registrationState.verificationEmail}</strong>
              </p>
              <p style={{ color: '#666', marginBottom: '2rem' }}>
                Please click the link in your email to verify your account and complete registration.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Check Again
                </button>
                <button
                  onClick={onBackToLogin}
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Back to Login
                </button>
              </div>
            </div>
          ) : (
            renderCurrentStep()
          )}
        </Content>
      </RegistrationCard>
    </RegistrationContainer>
  );
};
